import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Box,
  HStack,
  Text,
  Spinner,
  useToast,
  Tag,
  Flex,
  Image,
  useBreakpointValue,
} from "@chakra-ui/react";

const EthSignalsBar = () => {
  const [signals, setSignals] = useState([]);
  const [loading, setLoading] = useState(false);
  const toast = useToast();

  const isMobile = useBreakpointValue({ base: true, md: false });

  const fetchTradeSignals = async () => {
    setLoading(true);
    try {
      const apiUrl = "https://blindfoldcapital.com/api/ta/recent_trade_signals";
      const response = await axios.get(apiUrl);

      if (response.data && Array.isArray(response.data.data)) {
        const parsed = response.data.data.map((entry) => ({
          interval: entry.interval,
          direction: entry.direction,
          signal: entry.signal,
          timestamp: entry.timestamp,
        }));

        const allIntervals = ["1m", "5m", "30m", "1h", "4h", "1d"];
        const visibleIntervals = isMobile ? ["1m", "5m", "1h"] : allIntervals;

        const mostRecentPerInterval = visibleIntervals
          .map(
            (intv) =>
              parsed
                .filter((s) => s.interval === intv)
                .sort(
                  (a, b) => new Date(b.timestamp) - new Date(a.timestamp),
                )[0],
          )
          .filter(Boolean);

        setSignals(mostRecentPerInterval);
      } else {
        throw new Error("Invalid response format");
      }
    } catch (err) {
      toast({
        title: "Failed to fetch ETH signals",
        description: err.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTradeSignals();
    const interval = setInterval(fetchTradeSignals, 60 * 1000); // 60 seconds
    return () => clearInterval(interval);
  }, [isMobile]); // Refetch signals if screen size changes

  const getDirectionColor = (direction) => {
    switch (direction) {
      case "Uptrend":
        return "green";
      case "Downtrend":
        return "red";
      case "Sideways":
        return "gray";
      default:
        return "whiteAlpha";
    }
  };

  return (
    <Box w="100%" p={1} bg="gray.300">
      <Flex justify="space-between" align="center">
        <HStack gap="8px">
          <Image src="./eth.png" w="25px" />
          {loading ? (
            <Flex align="center" justify="center" py={1}>
              <Spinner size="sm" />
            </Flex>
          ) : (
            <HStack gap="3px" w="100%">
              {signals.map((sig, idx) => (
                <Tag
                  key={idx}
                  size='sm'
                  colorScheme={getDirectionColor(sig.direction)}
                  variant="solid"
                  px={0.5}
                  py={0.5}
                  minW="60px"
                  justifyContent="center"
                  borderRadius="sm"
                  whiteSpace="nowrap"
                >
                  <Text fontSize="xs" fontFamily="Sentinel-light">
                    {sig.interval.toUpperCase()}: {sig.direction}
                  </Text>
                </Tag>
              ))}
            </HStack>
          )}
        </HStack>
      </Flex>
    </Box>
  );
};

export default EthSignalsBar;
